import { createFileRoute, useParams } from "@tanstack/react-router";

import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { MainLayout } from "@/components/layouts/main-layout";
import { ErrorCard } from "@/components/shared/errors/error-card";
import { EditPdfContainer } from "@/components/shared/pdf-tools/edit-pdf";
import { InvoiceStatus } from "@/lib/constants/invoiceStatus";
import { usePreviousRoute } from "@/lib/miscellaneous/RouteTracker";
import { useInvoiceBrowser } from "@/providers/invoice-browser";
import { useInvoicePdfCache } from "@/providers/invoice-pdf-cache";
import { useEditPages } from "@/queries/invoices/edit-pages";
import { useGetInvoice } from "@/queries/invoices/get";

export const Route = createFileRoute(
  "/_authenticated/$company/unternehmen/_focused/rechnung/$id/seiten-bearbeiten",
)({
  component: PdfSplit,
});

function PdfSplit() {
  const { t } = useTranslation();

  const [pdfs, setPdfs] = useState<
    { invoiceId: string; fileName: string; pdfUrl: string }[]
  >([]);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [previousRoute, setPreviousRoute] = useState<string | null>(null);
  const _previousRoute = usePreviousRoute();

  const { editPages } = useEditPages();

  useEffect(() => {
    setPreviousRoute(_previousRoute);
  }, [_previousRoute]);

  const isPreviousRouteFastlane = previousRoute?.includes("fastlane") ?? false;

  const { id: invoiceId } = useParams({ from: Route.id });

  const { invoice, loading } = useGetInvoice({
    invoiceId,
  });

  const { fetchPdfUrl } = useInvoicePdfCache();

  const { remove: removeInvoiceFromQueue } = useInvoiceBrowser();

  useEffect(() => {
    const prefetchPdfs = async () => {
      try {
        const url = await fetchPdfUrl(invoiceId);

        setPdfs([
          {
            invoiceId,
            fileName: invoice?.invoiceDetails?.invoice?.fileName ?? "",
            pdfUrl: url,
          },
        ]);
      } catch (error) {
        console.error(error);
        Sentry.captureException(error);
        setErrorMessage(t("pdf_load_error"));
      }
    };

    if (!invoiceId) {
      return;
    }

    prefetchPdfs();
  }, [invoiceId, invoice?.invoiceDetails?.invoice?.status]);

  const handleNavigation = () => {
    // TODO: I don't like this. I would like to have a better way to handle this conditional navigation. One way could be to send the next route as query param. But I will not do it that now.
    if (!isPreviousRouteFastlane) {
      removeInvoiceFromQueue();

      setTimeout(() => {
        window.history.back();
      }, 50);
    } else {
      window.history.back();
    }
  };

  const handleSubmitLayout = async (
    layout: {
      invoiceId: string;
      pageIndex: number;
    }[][],
  ) => {
    await editPages(layout);
    handleNavigation();
  };

  const isProcessing =
    loading || invoice?.invoiceDetails?.invoice?.status === InvoiceStatus.NEW;

  if (errorMessage) {
    return (
      <div className="flex justify-center items-center flex-1">
        <ErrorCard errorMessage={errorMessage} title="Achtung" />
      </div>
    );
  }

  return (
    <EditPdfContainer
      sourceInvoiceId={invoiceId}
      initialPdfBlobs={pdfs}
      onSubmit={handleSubmitLayout}
      isProcessing={isProcessing}
    />
  );
}
